import { useEffect } from 'react';
import { base44 } from '@/api/base44Client';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SCRIPT_ID = 'localbusiness-jsonld';

function buildLocalBusiness({ venue, service }) {
  const phone = service?.phone_number || '+44 151 528 3001';
  const website = service?.website_url || 'https://motionreality.co.uk';
  const openTime = venue?.open_time || '09:00';
  const closeTime = venue?.close_time || '20:00';
  const openDays = venue?.open_days && venue.open_days.length
    ? venue.open_days
    : [1, 2, 3, 4, 5, 6]; // default Mon–Sat

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${website}#business`,
    name: 'Motion Reality',
    image: 'https://media.base44.com/images/public/69e75b1fdf3eee5e6d1453dc/de63d0e06_generated_66d7d18e.png',
    url: website,
    telephone: phone,
    email: 'info@motionreality.co.uk',
    priceRange: '££',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 9, Croft Business Park, Carrock Road',
      addressLocality: 'Bromborough',
      addressRegion: 'Wirral',
      postalCode: 'CH62 3RA',
      addressCountry: 'GB',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 53.353,
      longitude: -3.001,
    },
    openingHoursSpecification: openDays.map((d) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: DAY_NAMES[d],
      opens: openTime,
      closes: closeTime,
    })),
    description:
      'Premium motion racing simulator hardware store and cockpit booking platform. Custom sim racing rigs, motion platforms, wheels, pedals and bookable sim racing experiences in Bromborough, near Liverpool.',
  };
}

/**
 * Injects a LocalBusiness JSON-LD schema on the homepage so Google recognises
 * Motion Reality as a physical business in Bromborough, improving local search
 * visibility. The schema script is removed when the homepage unmounts so it
 * only ever appears on "/".
 */
export default function LocalBusinessSchema() {
  useEffect(() => {
    let active = true;
    Promise.all([
      base44.entities.VenueSettings.filter({}).then((r) => r?.[0]).catch(() => null),
      base44.entities.CustomerServiceSettings.filter({}).then((r) => r?.[0]).catch(() => null),
    ]).then(([venue, service]) => {
      if (!active) return;
      let script = document.getElementById(SCRIPT_ID);
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(buildLocalBusiness({ venue, service }));
    });
    return () => {
      active = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, []);
  return null;
}